import { useEffect, useMemo, useRef, useState } from "react";
import { Info, SlidersHorizontal } from "lucide-react";
import { DataTable, type Column } from "../../components/DataTable";
import { MultiSelectDropdown } from "../../components/MultiSelectDropdown";
import { SegmentedTabs } from "../../components/SegmentedTabs";
import { StatusBadge } from "../../components/StatusBadge";
import { TableToolbar } from "../../components/TableToolbar";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import {
  FINDINGS_LIST_FETCHERS,
  fetchFindingsTabCount,
  type FindingsTabCount,
  type LogReviewListParams,
} from "../../../api/logReviewFindingsApi";
import {
  fetchInfoDetails,
  fetchReasonDetails,
  fetchServerNodes,
} from "../../../api/logReviewPopupApi";
import { submitManageCompliance } from "../../../api/logReviewComplianceApi";
import { useToastStore } from "../../../stores/toastStore";
import type {
  LogReviewFilterKey,
  LogReviewJustificationHistory,
  LogReviewMultiFilters,
  LogReviewRow,
  LogReviewServerNode,
  LogReviewSessionDetail,
  LogReviewStatusTab,
  ManageCompliancePayload,
} from "../../types/logReview";
import {
  LogReviewFilterDrawer,
  type DrawerFilterFieldConfig,
} from "./LogReviewFilterDrawer";
import { LogReviewDetailsModal } from "./LogReviewDetailsModal";
import { LogReviewInfoModal } from "./LogReviewInfoModal";
import { LogReviewServersModal } from "./LogReviewServersModal";
import { ManageComplianceModal } from "./ManageComplianceModal";
import { riskVariant } from "./logReviewChartUtils";

const DEFAULT_SORT_COLUMN = "updatedDt";

/** UI column id → backend sort column for the findings list payload. */
const FINDINGS_SORT_COLUMNS: Record<string, string> = {
  changeRequestId: "changeRequestId",
  application: "application",
  // teamLead: "applicationOwner", — hidden; see the column definition below.
  division: "division",
  userName: "users",
  category: "category",
  team: "team",
  risk: "risk",
  plannedStart: "plannedStartTime",
  plannedEnd: "plannedEndTime",
  actualStart: "actualStartTime",
  actualEnd: "actualEndTime",
  servers: "servers",
  status: "status",
  reasonForNonCompliance: "reasonForNonCompliance",
};

/** Base list params (top filter bar) merged into the server-side fetch. */
export interface LogReviewListBaseParams {
  appCode: string[];
  category: string[];
  division: string[];
  startDate: string;
  endDate: string;
}

const TABS: { id: LogReviewStatusTab; label: string }[] = [
  { id: "NonCompliantFindings", label: "Non-Compliant Findings" },
  {
    id: "NonCompliantAcknowledgedFindings",
    label: "Non-Compliant (Acknowledged) Findings",
  },
  { id: "CompliantFindings", label: "Compliant Findings" },
];

/** Master column id list — covers every column any tab can show. */
const ALL_COLUMN_IDS = [
  "changeRequestId",
  "application",
  "division",
  "userName",
  "category",
  "team",
  // "teamLead", — Application Owner hidden; see the column definition below.
  "risk",
  "plannedStart",
  "plannedEnd",
  "actualStart",
  "actualEnd",
  "servers",
  "status",
  "reasonForNonCompliance",
  "info",
] as const;

/**
 * Per-tab default column visibility. Columns not listed start hidden and are
 * reachable through "More Fields"; the listed ones are pinned and can't be
 * toggled off.
 */
const TAB_VISIBLE_DEFAULTS: Record<LogReviewStatusTab, string[]> = {
  NonCompliantFindings: [
    "changeRequestId",
    "application",
    "division",
    "plannedStart",
    "plannedEnd",
    "servers",
    "status",
    "reasonForNonCompliance",
    "info",
  ],
  NonCompliantAcknowledgedFindings: [
    "changeRequestId",
    "application",
    "division",
    "plannedStart",
    "plannedEnd",
    "servers",
    "status",
    "reasonForNonCompliance",
    "info",
  ],
  CompliantFindings: [
    "changeRequestId",
    "application",
    "plannedStart",
    "plannedEnd",
    "servers",
  ],
};

/**
 * Columns that EXIST per tab. The Compliant tab drops Status / Reason / Info
 * entirely — not even toggleable — leaving its pinned defaults plus the audit
 * fields offered under "More Fields".
 */
const TAB_AVAILABLE_COLUMNS: Record<LogReviewStatusTab, readonly string[]> = {
  NonCompliantFindings: ALL_COLUMN_IDS,
  NonCompliantAcknowledgedFindings: ALL_COLUMN_IDS,
  CompliantFindings: [
    "changeRequestId",
    "application",
    "plannedStart",
    "plannedEnd",
    "servers",
    "actualStart",
    "actualEnd",
    "category",
    "division",
    "risk",
    "team",
    // "teamLead", — Application Owner hidden; see the column definition below.
    "userName",
  ],
};

const COLUMN_LABELS: Record<string, string> = {
  changeRequestId: "CR Number",
  application: "Application",
  // teamLead: "Application Owner", — hidden; see the column definition below.
  division: "Division",
  userName: "User ID",
  category: "Category",
  team: "Team",
  risk: "Risk",
  plannedStart: "Planned Start",
  plannedEnd: "Planned End",
  actualStart: "Actual Start",
  actualEnd: "Actual End",
  servers: "Servers/Nodes",
  status: "Status",
  reasonForNonCompliance: "Reason(s) for Non-Compliance",
  info: "Info",
};

const buildVisibility = (tab: LogReviewStatusTab): Record<string, boolean> => {
  const defaults = TAB_VISIBLE_DEFAULTS[tab];
  const vis: Record<string, boolean> = {};
  ALL_COLUMN_IDS.forEach((id) => {
    vis[id] = defaults.includes(id);
  });
  return vis;
};

export interface LogReviewTableProps {
  filterFields: readonly DrawerFilterFieldConfig[];
  /** Top filter-bar selection merged into the server-side list fetch. */
  listBaseParams: LogReviewListBaseParams;
  /** Bumped on every top-bar Filter/Clear to force a re-fetch. */
  listReloadKey?: number;
  /**
   * Gates the server-side list fetch until the top filter cascade has applied
   * — avoids a first request with empty default filters that the cascade's
   * initial apply would abort and refire.
   */
  enabled?: boolean;
}

/**
 * The "Details" table on the main Log Review dashboard: three server-driven
 * status tabs with per-tab column sets, a facet drawer, row selection for the
 * compliance workflow, and four per-CR drill-down popups.
 */
export const LogReviewTable = ({
  filterFields,
  listBaseParams,
  listReloadKey = 0,
  enabled = true,
}: LogReviewTableProps) => {
  const showError = useToastStore((s) => s.showError);
  const showSuccess = useToastStore((s) => s.showSuccess);

  const [tab, setTab] = useState<LogReviewStatusTab>("NonCompliantFindings");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  // The typed box, and the keyword actually sent to the backend. Split so a
  // keystroke never reaches the API — the second only moves on submit.
  const [search, setSearch] = useState("");
  const [keyword, setKeyword] = useState("");
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const [manageOpen, setManageOpen] = useState(false);
  const [manageSubmitting, setManageSubmitting] = useState(false);
  const [localReload, setLocalReload] = useState(0);
  const [detailRow, setDetailRow] = useState<LogReviewRow | null>(null);
  const [infoRow, setInfoRow] = useState<LogReviewRow | null>(null);
  const [serverRow, setServerRow] = useState<LogReviewRow | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState<LogReviewMultiFilters>({});
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(
    () => buildVisibility("NonCompliantFindings"),
  );

  // Server-side list state.
  const [serverRows, setServerRows] = useState<LogReviewRow[]>([]);
  const [serverTotal, setServerTotal] = useState(0);
  const [serverLoading, setServerLoading] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  // Per-tab totals (single call) shown as the tab count badges.
  const [tabCountData, setTabCountData] = useState<FindingsTabCount | null>(null);

  // Lazily-fetched popup payloads (reason / servers / info).
  const [detailSessions, setDetailSessions] = useState<LogReviewSessionDetail[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [serverNodes, setServerNodes] = useState<LogReviewServerNode[]>([]);
  const [serverNodesLoading, setServerNodesLoading] = useState(false);
  const [infoHistory, setInfoHistory] = useState<LogReviewJustificationHistory[]>([]);
  const [infoLoading, setInfoLoading] = useState(false);

  const isNonCompliantTab = tab === "NonCompliantFindings";

  // Applying the top filter resets the table's own state — active tab, page,
  // keyword, sort, drawer facets and the "More Fields" selection — so the new
  // result set starts clean. The explicit visibility reset matters when the
  // user is ALREADY on the initial tab: `setTab` is a no-op then, so the
  // tab-change effect below never rebuilds it. Skipped on first mount.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    setTab("NonCompliantFindings");
    setColumnVisibility(buildVisibility("NonCompliantFindings"));
    setPageIndex(0);
    // Both halves: clearing only the box would leave the committed keyword
    // still filtering the results it no longer appears to describe.
    setSearch("");
    setKeyword("");
    setSortKey(null);
    setFilters({});
  }, [listReloadKey]);

  const teamFilterKey = (filters.team ?? []).join(",");
  const riskFilterKey = (filters.risk ?? []).join(",");
  const statusFilterKey = (filters.status ?? []).join(",");

  // No active column sort ⇒ send empty sort fields; the backend then applies
  // its own default ordering.
  const mappedSort = sortKey ? FINDINGS_SORT_COLUMNS[sortKey] : undefined;
  const sortByColumn = sortKey ? (mappedSort ?? DEFAULT_SORT_COLUMN) : "";
  const sortOrder: "asc" | "desc" | "" = sortKey ? sortDir : "";

  // asc → desc → unsorted. The third click clears the sort so the backend
  // reapplies its own default ordering.
  const handleSort = (key: string) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      setSortKey(null);
      setSortDir("asc");
    }
    setPageIndex(0);
  };

  /**
   * Commit the typed box to the backend keyword. Typing alone never hits the
   * API; this runs on Enter, on the search button, and when the box is
   * cleared. Bails when nothing changed so a stray Enter is not a refetch.
   */
  const submitSearch = (value: string) => {
    const next = value.trim();
    if (next === keyword) return;
    setKeyword(next);
    setPageIndex(0);
  };

  // Every tab is API-backed (Non-Compliant / Acknowledged / Compliant).
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    setServerLoading(true);
    setServerError(null);
    const params: LogReviewListParams = {
      keyword,
      sortByColumn,
      sortOrder,
      appCode: listBaseParams.appCode,
      category: listBaseParams.category,
      teams: filters.team ?? [],
      division: listBaseParams.division,
      username: [],
      risk: filters.risk ?? [],
      status: filters.status ?? [],
      startDate: listBaseParams.startDate,
      endDate: listBaseParams.endDate,
    };
    FINDINGS_LIST_FETCHERS[tab](params, pageIndex, pageSize, controller.signal)
      .then((res) => {
        if (controller.signal.aborted) return;
        setServerRows(res.rows);
        setServerTotal(res.totalCount);
        setServerLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        const message = err instanceof Error ? err.message : "Failed to load findings";
        setServerError(message);
        showError("Log Review", message);
        setServerLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    tab,
    keyword,
    sortByColumn,
    sortOrder,
    pageIndex,
    pageSize,
    teamFilterKey,
    riskFilterKey,
    statusFilterKey,
    listBaseParams,
    listReloadKey,
    localReload,
  ]);

  // Tab counts — one call returns all three totals. Independent of the active
  // tab, pagination and sort; re-fetches on filter / search / reload changes.
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    fetchFindingsTabCount(
      {
        keyword,
        appCode: listBaseParams.appCode,
        category: listBaseParams.category,
        teams: filters.team ?? [],
        division: listBaseParams.division,
        username: [],
        risk: filters.risk ?? [],
        status: [],
        startDate: listBaseParams.startDate,
        endDate: listBaseParams.endDate,
      },
      controller.signal,
    )
      .then((counts) => {
        if (controller.signal.aborted) return;
        setTabCountData(counts);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        showError(
          "Log Review",
          err instanceof Error ? err.message : "Failed to load tab counts",
        );
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    keyword,
    teamFilterKey,
    riskFilterKey,
    listBaseParams,
    listReloadKey,
    localReload,
  ]);

  // Reason popup — fetch session details by CR + appCode when opened.
  useEffect(() => {
    if (!detailRow) return;
    const controller = new AbortController();
    setDetailLoading(true);
    setDetailSessions([]);
    fetchReasonDetails(detailRow.changeRequestId, detailRow.application, controller.signal)
      .then((rows) => {
        if (controller.signal.aborted) return;
        setDetailSessions(rows);
        setDetailLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        showError(
          "Log Review",
          err instanceof Error ? err.message : "Failed to load details",
        );
        setDetailLoading(false);
      });
    return () => controller.abort();
  }, [detailRow, showError]);

  // Servers/Nodes popup — fetch by finding id when opened.
  useEffect(() => {
    if (!serverRow) return;
    const controller = new AbortController();
    setServerNodesLoading(true);
    setServerNodes([]);
    fetchServerNodes(serverRow.id, controller.signal)
      .then((rows) => {
        if (controller.signal.aborted) return;
        setServerNodes(rows);
        setServerNodesLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        showError(
          "Log Review",
          err instanceof Error ? err.message : "Failed to load servers",
        );
        setServerNodesLoading(false);
      });
    return () => controller.abort();
  }, [serverRow, showError]);

  // Info popup — fetch justification history by finding id when opened.
  useEffect(() => {
    if (!infoRow) return;
    const controller = new AbortController();
    setInfoLoading(true);
    setInfoHistory([]);
    fetchInfoDetails(infoRow.id, controller.signal)
      .then((rows) => {
        if (controller.signal.aborted) return;
        setInfoHistory(rows);
        setInfoLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        showError("Log Review", err instanceof Error ? err.message : "Failed to load info");
        setInfoLoading(false);
      });
    return () => controller.abort();
  }, [infoRow, showError]);

  useEffect(() => {
    setSelection({});
  }, [tab, filters, serverRows]);

  // Reset visibility to the per-tab default whenever the tab changes — each
  // tab has its own visible/hidden split.
  useEffect(() => {
    setColumnVisibility(buildVisibility(tab));
    // Status isn't a concept on the Compliant tab — drop any applied Status
    // filter so it doesn't silently constrain the rows.
    if (tab === "CompliantFindings") {
      setFilters((prev) => {
        if (!prev.status) return prev;
        const { status: _status, ...rest } = prev;
        return rest;
      });
    }
  }, [tab]);

  // Only rows still Open can be justified, and only on the Non-Compliant tab.
  const isRowSelectable = (row: LogReviewRow) =>
    isNonCompliantTab && row.status === "Open";

  const selectableRows = useMemo(
    () => serverRows.filter(isRowSelectable),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serverRows, isNonCompliantTab],
  );

  const selectedIds = useMemo(
    () =>
      Object.entries(selection)
        .filter(([, v]) => v)
        .map(([k]) => k),
    [selection],
  );

  const allSelected =
    selectableRows.length > 0 && selectableRows.every((r) => selection[r.id]);

  const toggleAll = (checked: boolean) => {
    setSelection(() => {
      if (!checked) return {};
      const next: Record<string, boolean> = {};
      selectableRows.forEach((r) => {
        next[r.id] = true;
      });
      return next;
    });
  };

  const allColumns = useMemo<Column<LogReviewRow>[]>(() => {
    const cols: Column<LogReviewRow>[] = [
      {
        key: "changeRequestId",
        cellClassName: "!whitespace-nowrap",
        header: "CR Number",
        sortable: true,
        align: "center",
        headerAlign: "center",
      },
      { key: "application", cellClassName: "!whitespace-nowrap", align: "left", header: "Application", sortable: true, headerAlign: "center" },
      // Application Owner is hidden for now. The row data still carries
      // `teamLead` (mapped from the backend's `applicationOwner`), so
      // re-enabling is display-only: uncomment this block plus the four
      // `teamLead` entries marked below — ALL_COLUMN_IDS and
      // TAB_AVAILABLE_COLUMNS matter most, since leaving the id in either one
      // would list "Application Owner" under More Fields with no column to
      // toggle.
      // {
      //   key: "teamLead",
      //   align: "left",
      //   header: "Application Owner",
      //   sortable: true,
      //   headerAlign: "center",
      // },
      { key: "division", cellClassName: "!whitespace-nowrap", align: "left", header: "Division", sortable: true, headerAlign: "center" },
      { key: "userName", align: "left", header: "User ID", sortable: true, headerAlign: "center" },
      { key: "category", align: "left", header: "Category", sortable: true, headerAlign: "center" },
      { key: "team", align: "left", header: "Team", sortable: true, headerAlign: "center" },
      {
        key: "risk",
        header: "Risk",
        sortable: true,
        align: "center",
        headerAlign: "center",
        render: (row) => (
          <StatusBadge status={riskVariant(row.risk)} label={row.risk} dot={false} />
        ),
      },
      {
        key: "plannedStart",
        cellClassName: "!whitespace-nowrap",
        header: "Planned Start",
        sortable: true,
        align: "center",
        headerAlign: "center",
      },
      {
        key: "plannedEnd",
        cellClassName: "!whitespace-nowrap",
        header: "Planned End",
        sortable: true,
        align: "center",
        headerAlign: "center",
      },
      {
        key: "actualStart",
        cellClassName: "!whitespace-nowrap",
        header: "Actual Start",
        sortable: true,
        align: "center",
        headerAlign: "center",
      },
      {
        key: "actualEnd",
        cellClassName: "!whitespace-nowrap",
        header: "Actual End",
        sortable: true,
        align: "center",
        headerAlign: "center",
      },
      {
        key: "servers",
        cellClassName: "!whitespace-nowrap",
        header: "Servers/Nodes",
        sortable: true,
        align: "center",
        headerAlign: "center",
        render: (row) => (
          <button
            type="button"
            className="text-primary underline underline-offset-2"
            onClick={() => setServerRow(row)}
          >
            {row.servers}
          </button>
        ),
      },
      {
        key: "status",
        cellClassName: "!whitespace-nowrap",
        header: "Status",
        sortable: true,
        align: "center",
        headerAlign: "center",
      },
      {
        key: "reasonForNonCompliance",
        align: "left",
        header: "Reason(s) for Non-Compliance",
        sortable: true,
        headerAlign: "center",
        maxChars: false,
        render: (row) => {
          // Reasons arrive comma-joined; list each on its own line. Still
          // clickable to open the session-level details popup.
          const parts = row.reasonForNonCompliance
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
          if (!parts.length) return "—";
          return (
            <button
              type="button"
              className="flex flex-col gap-0.5 text-left text-primary underline underline-offset-2"
              onClick={() => setDetailRow(row)}
            >
              {parts.map((reason, i) => (
                <span key={`${reason}-${i}`}>
                  {reason}
                  {i < parts.length - 1 ? "," : ""}
                </span>
              ))}
            </button>
          );
        },
      },
      {
        key: "info",
        header: "Info",
        align: "center",
        headerAlign: "center",
        render: (row) => (
          <button
            type="button"
            aria-label={`Information for ${row.changeRequestId}`}
            data-testid={`lr-info-${row.id}`}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={() => setInfoRow(row)}
          >
            <Info className="size-4" aria-hidden />
          </button>
        ),
      },
    ];
    return cols;
  }, []);

  // Restrict to the columns that exist for the active tab, then apply the
  // "More Fields" visibility, then prepend the selection checkbox when the
  // compliance workflow is available.
  const visibleColumns = useMemo(() => {
    const available = TAB_AVAILABLE_COLUMNS[tab];
    const cols = allColumns.filter(
      (c) => available.includes(c.key) && columnVisibility[c.key] !== false,
    );
    if (!isNonCompliantTab) return cols;

    const selectColumn: Column<LogReviewRow> = {
      key: "__select",
      // No explicit `width`: a single sized column puts the whole table into
      // table-fixed, which then splits every remaining column evenly and
      // squeezes the wide ones (Reason, Application) until their text breaks
      // mid-word. `minWidth` floors this column without triggering that.
      minWidth: 56,
      align: "center",
      headerAlign: "center",
      header: (
        <Checkbox
          checked={allSelected}
          onCheckedChange={(v) => toggleAll(v === true)}
          aria-label="Select all selectable rows"
          disabled={selectableRows.length === 0}
        />
      ),
      render: (row) => (
        <Checkbox
          checked={selection[row.id] === true}
          disabled={!isRowSelectable(row)}
          aria-label={`Select ${row.changeRequestId}`}
          onCheckedChange={(v) =>
            setSelection((prev) => ({ ...prev, [row.id]: v === true }))
          }
        />
      ),
    };
    return [selectColumn, ...cols];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allColumns, tab, columnVisibility, isNonCompliantTab, selection, allSelected, selectableRows]);

  // Columns offered under "More Fields" — everything the tab supports minus
  // its pinned defaults.
  const toggleableColumns = useMemo(
    () =>
      TAB_AVAILABLE_COLUMNS[tab].filter((id) => !TAB_VISIBLE_DEFAULTS[tab].includes(id)),
    [tab],
  );

  // Sorted by LABEL, not by the column order: this is a lookup list, and the
  // reader is scanning for a field name they already have in mind.
  const columnOptions = useMemo(
    () =>
      toggleableColumns
        .map((id) => ({ value: id, label: COLUMN_LABELS[id] ?? id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [toggleableColumns],
  );

  const visibleToggleableColumns = useMemo(
    () => toggleableColumns.filter((id) => columnVisibility[id] === true),
    [toggleableColumns, columnVisibility],
  );

  // The dropdown hands back the full set of checked ids, so every toggleable
  // column is restated -- present ones on, absent ones off. Patching only the
  // ones that changed would leave a stale `true` behind on tab switches.
  const handleColumnSelectionChange = (next: string[]) => {
    setColumnVisibility((prev) => {
      const merged = { ...prev };
      toggleableColumns.forEach((id) => {
        merged[id] = next.includes(id);
      });
      return merged;
    });
  };

  // Only the Non-Compliant tab needs the Status facet — the Acknowledged and
  // Compliant tabs each represent a single status.
  const tabFilterFields = useMemo(
    () =>
      tab === "NonCompliantFindings"
        ? filterFields
        : filterFields.filter((f) => f.key !== "status"),
    [filterFields, tab],
  );

  const tabCounts = useMemo(
    () =>
      new Map<LogReviewStatusTab, number>(
        // All three totals come from the tab-count API; until it resolves,
        // show the active tab's live total and leave the others blank.
        TABS.map((t) => [
          t.id,
          tabCountData ? tabCountData[t.id] : t.id === tab ? serverTotal : 0,
        ]),
      ),
    [tabCountData, serverTotal, tab],
  );

  const filterCount = useMemo(() => {
    let n = 0;
    (Object.keys(filters) as LogReviewFilterKey[]).forEach((k) => {
      if ((filters[k] ?? []).length > 0) n += 1;
    });
    return n;
  }, [filters]);

  const handleManageSubmit = (payload: ManageCompliancePayload) => {
    setManageSubmitting(true);
    submitManageCompliance(payload.rowIds, payload.justification, payload.attachments)
      .then((res) => {
        const message = res.message || "Justification submitted";
        if (res.success) {
          showSuccess("Manage Compliance", message);
          setManageOpen(false);
          setSelection({});
          setLocalReload((k) => k + 1);
        } else {
          showError("Manage Compliance", message);
        }
      })
      .catch((err: unknown) => {
        showError(
          "Manage Compliance",
          err instanceof Error ? err.message : "Failed to submit justification",
        );
      })
      .finally(() => setManageSubmitting(false));
  };

  const handleTabChange = (id: LogReviewStatusTab) => {
    setTab(id);
    setPageIndex(0);
    setSearch("");
    setSortKey(null);
    setFilters({});
  };

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-foreground">Details</h3>

      {/* Tabs get a row of their own above the toolbar: they pick WHICH set
          of findings is being looked at, while everything in the toolbar acts
          on the set already chosen. Sharing a row put a view switch and a
          row action side by side as if they were peers. */}
      <SegmentedTabs
        items={TABS.map((t) => ({
          id: t.id,
          label: t.label,
          count: tabCounts.get(t.id) ?? 0,
        }))}
        value={tab}
        onChange={(id) => handleTabChange(id as LogReviewStatusTab)}
        ariaLabel="Findings compliance state"
      />

      {/* The toolbar carries `flex-1` for the case where it shares a row with
          a sibling cluster. This parent is a flex COLUMN, so that grows it
          vertically instead and its wrapped lines drift apart across the free
          height. Clamping to content stops the growth and hands the space to
          the table below, where it belongs. */}
      <TableToolbar
        className="max-h-fit"
        search={{
          value: search,
          // Typing only updates the box. `onSubmit` is what reaches the API,
          // matching every other table in the app — the toolbar fires it on
          // Enter, on the search button, and the moment the box is cleared.
          onChange: setSearch,
          onSubmit: submitSearch,
          placeholder: "Search",
          ariaLabel: "Search findings",
        }}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPageIndex(0);
        }}
        actions={
          <>
            {/* `bg-card` overrides the outline variant's `bg-background`. That
                default is right for an outline button inside a dialog or a
                popover, which are already white -- but this toolbar sits on
                the page, so bg-background paints the button the colour of the
                bar behind it. The rest of this row is bg-card. */}
            <Button
              variant="outline"
              size="sm"
              className="bg-card"
              onClick={() => setFiltersOpen(true)}
            >
              <SlidersHorizontal className="size-4" aria-hidden />
              Filters
              {filterCount > 0 ? (
                <span className="ml-1 rounded-full bg-primary px-1.5 py-0.5 text-xs tabular-nums text-primary-foreground">
                  {filterCount}
                </span>
              ) : null}
            </Button>

            {/* The column picker reuses the filter dropdown rather than a bare
                menu, so it gets the search box and Select all / Clear all that
                a list this long needs. */}
            {toggleableColumns.length > 0 ? (
              <MultiSelectDropdown
                trigger={
                  <Button variant="outline" size="sm" className="bg-card">
                    <SlidersHorizontal className="size-4" aria-hidden />
                    More Fields
                  </Button>
                }
                selectAll
                options={columnOptions}
                selected={visibleToggleableColumns}
                onChange={handleColumnSelectionChange}
                searchable
              />
            ) : null}

            {isNonCompliantTab ? (
              <Button
                size="sm"
                disabled={selectedIds.length === 0}
                onClick={() => setManageOpen(true)}
              >
                {selectedIds.length > 0
                  ? `Manage Compliance (${selectedIds.length})`
                  : "Manage Compliance"}
              </Button>
            ) : null}
          </>
        }
      />

      <DataTable<LogReviewRow>
        columns={visibleColumns}
        data={serverRows}
        rowKey={(row) => row.id}
        loading={serverLoading}
        error={serverError}
        onRetry={() => setLocalReload((k) => k + 1)}
        emptyMessage="No Data Available"
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={handleSort}
        // DataTable's `page` is ZERO-based, as every other table in the app
        // passes it. Handing it `pageIndex + 1` made it read page 0 as page 1:
        // the footer said "Page 2 of 7" and "Showing 11-20" while the first ten
        // rows were on screen, and First/Prev drove pageIndex negative.
        page={pageIndex}
        pageSize={pageSize}
        totalItems={serverTotal}
        onPageChange={setPageIndex}
        pagerStyle="simple"
        itemLabel="findings"
      />

      <LogReviewFilterDrawer
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        fields={tabFilterFields}
        applied={filters}
        onApply={(nextFilters) => {
          setFilters(nextFilters);
          setPageIndex(0);
        }}
      />

      <ManageComplianceModal
        open={manageOpen}
        onOpenChange={setManageOpen}
        selectedIds={selectedIds}
        submitting={manageSubmitting}
        onSubmit={handleManageSubmit}
      />

      <LogReviewDetailsModal
        open={detailRow !== null}
        row={detailRow ? { ...detailRow, sessionDetails: detailSessions } : null}
        isLoading={detailLoading}
        onOpenChange={(open) => {
          if (!open) setDetailRow(null);
        }}
      />

      <LogReviewInfoModal
        open={infoRow !== null}
        row={infoRow ? { ...infoRow, justificationHistory: infoHistory } : null}
        isLoading={infoLoading}
        onOpenChange={(open) => {
          if (!open) setInfoRow(null);
        }}
      />

      <LogReviewServersModal
        open={serverRow !== null}
        row={serverRow ? { ...serverRow, serverNodes } : null}
        isLoading={serverNodesLoading}
        onOpenChange={(open) => {
          if (!open) setServerRow(null);
        }}
      />
    </div>
  );
};
